import React from 'react';
import styled, { keyframes } from 'styled-components';
import headerIcon from '../assets/images/loading-icon.jpg';

const LandingPage = ({ onEnterApp }) => {
  return (
    <LandingContainer>
      <HeroSection>
        <LogoContainer>
          <Logo src={headerIcon} alt="Stoic Wisdom" />
          <LogoGlow />
        </LogoContainer>
        <Title>Stoic Wisdom</Title>
        <Subtitle>Ancient wisdom for modern resilience</Subtitle>
        
        <Description>
          Discover timeless Stoic philosophy quotes to guide you through life's challenges.
          Build resilience, find clarity, and develop inner strength with wisdom from the ancient Stoics.
        </Description>
        
        <ButtonContainer>
          <AuthButton onClick={() => onEnterApp('login')} login>
            Login
          </AuthButton>
          <AuthButton onClick={() => onEnterApp('signup')} signup>
            Sign Up
          </AuthButton>
        </ButtonContainer>
        
        <EnterAppLink onClick={() => onEnterApp('guest')}>
          Continue as Guest
        </EnterAppLink>
      </HeroSection>
      
      <FeaturesSection>
        <FeatureTitle>Discover Stoic Wisdom</FeatureTitle>
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>📚</FeatureIcon>
            <FeatureHeading>Curated Quotes</FeatureHeading>
            <FeatureText>Explore a collection of powerful quotes from Stoic philosophers.</FeatureText>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>⭐</FeatureIcon>
            <FeatureHeading>Save Favorites</FeatureHeading>
            <FeatureText>Bookmark your favorite quotes for daily inspiration.</FeatureText>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>🔥</FeatureIcon>
            <FeatureHeading>Define Your Villain</FeatureHeading>
            <FeatureText>Create a personified obstacle to overcome in your journey.</FeatureText>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>🤖</FeatureIcon>
            <FeatureHeading>AI Motivation</FeatureHeading>
            <FeatureText>Get direct, no-nonsense motivation when you need it most.</FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </FeaturesSection>
      
      <Footer>
        <FooterText>© {new Date().getFullYear()} Stoic Wisdom. All rights reserved.</FooterText>
      </Footer>
    </LandingContainer>
  );
};

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(212, 175, 55, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LandingContainer = styled.div`
  min-height: 100vh;
  background-color: var(--color-background);
  color: var(--color-text);
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 1rem;
  background: linear-gradient(to bottom, #000000, #111111);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, transparent, var(--color-gold), transparent);
  }
`;

const LogoContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  animation: ${fadeIn} 1s ease-out;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--color-gold);
  box-shadow: 0 0 20px rgba(212, 175, 55, 0.3);
  z-index: 1;
  position: relative;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const LogoGlow = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid var(--color-gold);
  opacity: 0.6;
  animation: ${pulse} 2s infinite;
`;

const Title = styled.h1`
  font-family: var(--font-primary);
  font-size: 3.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-gold);
  background: linear-gradient(to right, #BF953F, #FCF6BA, #B38728, #FBF5B7, #AA771C);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  letter-spacing: 3px;
  text-transform: uppercase;
  animation: ${fadeIn} 1.2s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--color-secondary);
  font-style: italic;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1.4s ease-out;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Description = styled.p`
  max-width: 600px;
  margin-bottom: 3rem;
  line-height: 1.8;
  color: var(--color-text);
  animation: ${fadeIn} 1.6s ease-out;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0 1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  animation: ${fadeIn} 1.8s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    width: 80%;
  }
`;

const AuthButton = styled.button`
  padding: 0.8rem 2.5rem;
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.login && `
    background: transparent;
    color: var(--color-primary);
    border: 2px solid var(--color-primary);
    
    &:hover {
      background: rgba(192, 192, 192, 0.1);
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    }
  `}
  
  ${props => props.signup && `
    background: linear-gradient(to right, var(--color-gold-dark), var(--color-gold), var(--color-gold-dark));
    color: #000;
    border: none;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(212, 175, 55, 0.4);
    }
  `}
  
  @media (max-width: 768px) {
    padding: 0.7rem 2rem;
    font-size: 1rem;
  }
`;

const EnterAppLink = styled.button`
  background: transparent;
  border: none;
  color: var(--color-secondary);
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.3s ease;
  animation: ${fadeIn} 2s ease-out;
  
  &:hover {
    color: var(--color-primary);
  }
`;

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background-color: #0a0a0a;
`;

const FeatureTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: var(--color-primary);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: linear-gradient(145deg, #0f0f0f, #1a1a1a);
  border: 1px solid #333;
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const FeatureHeading = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--color-gold);
`;

const FeatureText = styled.p`
  color: var(--color-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const Footer = styled.footer`
  padding: 1.5rem;
  background: #000;
  text-align: center;
  border-top: 1px solid #222;
  margin-top: auto;
`;

const FooterText = styled.p`
  color: var(--color-secondary);
  font-size: 0.8rem;
`;

export default LandingPage;
